// ═══════════════════════════════════════════════════════════════════════════════
// strategyUtils.js — Build, extract, and parse trading strategy conditions
// ═══════════════════════════════════════════════════════════════════════════════
//
// CHANGES IN THIS VERSION:
// - parseLogicToConditions now tolerates AI variations:
//     * Case-insensitive indicator names (rsi, RSI, Rsi all work)
//     * Strips quotes from params (RSI(14, 'close') → RSI(14))
//     * Fills in defaults for missing params (BB_Upper(20) → adds stdDev=2)
//     * Looser regex for names with underscores (OI_RSI, FundingRate, etc.)
// - INDICATOR_META expanded to cover all 40+ indicators in FUTURES_INDICATORS
// ═══════════════════════════════════════════════════════════════════════════════

import { parse as mathParse } from 'mathjs';

/**
 * Indicator metadata registry.
 * Each entry defines how the indicator appears in logic strings.
 *
 * This MUST stay in sync with:
 *   - backend INDICATOR_CATALOG (apiController.js)
 *   - frontend CANDLE_INDICATORS / FUTURES_INDICATORS / TICK_INDICATORS
 *     (components/conditionBuilder/StrategyBuilder.jsx)
 */
const INDICATOR_META = {
  // Session position (stock bots only). Not indicators — read off the bar's
  // timestamp against the real trading calendar, so a half-day counts down to
  // 13:00 rather than 16:00. Parameterless, like Price/VWAP.
  // `session: true` marks these as NOT indicators. They are supplied to the
  // expression scope from the bar's timestamp, so they must never reach the
  // indicator spec list — the engine has no implementation for a name like
  // "MinutesToClose_0_1m".
  MinutesSinceOpen: { params: [], defaults: {}, session: true },
  MinutesToClose:   { params: [], defaults: {}, session: true },
  Hour:             { params: [], defaults: {}, session: true },

  // Candle-based — Momentum
  RSI:        { params: ['period'],                         defaults: { period: 14 } },
  Stochastic: { params: ['kPeriod', 'dPeriod'],             defaults: { kPeriod: 14, dPeriod: 3 } },
  StochRSI:   { params: ['rsiPeriod', 'stochPeriod'],       defaults: { rsiPeriod: 14, stochPeriod: 14 } },

  // Candle-based — Trend
  EMA:        { params: ['period'],                         defaults: { period: 20 } },
  SMA:        { params: ['period'],                         defaults: { period: 50 } },
  MACD:       { params: ['fast', 'slow', 'signal'],         defaults: { fast: 12, slow: 26, signal: 9 } },
  SuperTrend: { params: ['period', 'multiplier'],           defaults: { period: 10, multiplier: 3 } },
  ADX:        { params: ['period'],                         defaults: { period: 14 } },

  // Candle-based — Volatility
  BB_Upper:       { params: ['period', 'stdDev'],                        defaults: { period: 20, stdDev: 2 } },
  BB_Lower:       { params: ['period', 'stdDev'],                        defaults: { period: 20, stdDev: 2 } },
  ATR:            { params: ['period'],                                  defaults: { period: 14 } },
  Keltner_Upper:  { params: ['emaPeriod', 'atrPeriod', 'multiplier'],    defaults: { emaPeriod: 20, atrPeriod: 10, multiplier: 2 } },
  Keltner_Mid:    { params: ['emaPeriod', 'atrPeriod', 'multiplier'],    defaults: { emaPeriod: 20, atrPeriod: 10, multiplier: 2 } },
  Keltner_Lower:  { params: ['emaPeriod', 'atrPeriod', 'multiplier'],    defaults: { emaPeriod: 20, atrPeriod: 10, multiplier: 2 } },

  // Candle-based — Volume
  Volume:     { params: [] },
  VWAP:       { params: [] },
  MFI:        { params: ['period'],                         defaults: { period: 14 } },
  OBV:        { params: [] },

  // Candle-based — Price
  Price:      { params: [] },

  // Tick-based
  tickPrice:       { params: [] },
  tickDelta:       { params: [] },
  tickVolume:      { params: [] },
  tickTradeVolume: { params: [] },
  tickRSI:         { params: ['period'],                    defaults: { period: 14 } },
  tickVWAP:        { params: [] },

  // Futures — Funding
  FundingRate:           { params: [] },
  FundingRateEMA:        { params: ['period'],              defaults: { period: 8 } },
  FundingRateSMA:        { params: ['period'],              defaults: { period: 8 } },
  FundingRateCumulative: { params: ['period'],              defaults: { period: 24 } },

  // Futures — Open Interest
  OpenInterest: { params: [] },
  OI_Delta:     { params: ['period'],                       defaults: { period: 5 } },
  OI_SMA:       { params: ['period'],                       defaults: { period: 14 } },
  OI_EMA:       { params: ['period'],                       defaults: { period: 14 } },
  OI_RSI:       { params: ['period'],                       defaults: { period: 14 } },
  VWOI:         { params: [] },

  // Futures — Sentiment
  LongShortRatio:    { params: [] },
  LongShortRatioEMA: { params: ['period'],                  defaults: { period: 8 } },

  // Futures — Mark / Index / Basis
  MarkPrice:          { params: [] },
  IndexPrice:         { params: [] },
  MarkIndexSpread:    { params: [] },
  MarkIndexSpreadEMA: { params: ['period'],                 defaults: { period: 14 } },
  Basis:              { params: [] },
  BasisPct:           { params: [] },
  BasisPctEMA:        { params: ['period'],                 defaults: { period: 14 } },

  // Futures — Risk
  LiqPrice:            { params: [] },
  LiquidationDistance: { params: ['leverage', 'maintRate'], defaults: { leverage: 10, maintRate: 0.004 } },
// Order Flow — CVD
  CVD:            { params: [] },
  CVD_EMA:        { params: ['period'],     defaults: { period: 14 } },
  CVD_Divergence: { params: ['lookback'],   defaults: { lookback: 20 } },

  // Order Flow — Delta
  Delta:          { params: [] },
  DeltaPercent:   { params: [] },
  DeltaMomentum:  { params: ['period'],     defaults: { period: 5 } },
  MaxDelta:       { params: ['lookback'],   defaults: { lookback: 20 } },
  MinDelta:       { params: ['lookback'],   defaults: { lookback: 20 } },

  // Order Flow — Aggressor
  AggressorRatio:    { params: ['period'],  defaults: { period: 100 } },
  AggressorRatioEMA: { params: ['period'],  defaults: { period: 50 } },
  BuyMarketVolume:   { params: ['period'],  defaults: { period: 20 } },
  SellMarketVolume:  { params: ['period'],  defaults: { period: 20 } },
  BuyVolumeChange:   { params: ['period'],  defaults: { period: 5 } },
  SellVolumeChange:  { params: ['period'],  defaults: { period: 5 } },
  TradeCount:        { params: ['period'],  defaults: { period: 60 } },
  AvgTradeSize:      { params: ['period'],  defaults: { period: 60 } },

  // Order Book
  BookImbalance: { params: ['depth'],   defaults: { depth: 10 } },
  BidAskRatio:   { params: ['depth'],   defaults: { depth: 10 } },
  Spread:        { params: [] },
  SpreadEMA:     { params: ['period'],  defaults: { period: 20 } },
  BookDepth:     { params: ['levels'],  defaults: { levels: 10 } },
  BookSlope:     { params: ['levels'],  defaults: { levels: 20 } },

  // Volume Profile
  POC:           { params: ['lookback'], defaults: { lookback: 100 } },
  VAH:           { params: ['lookback'], defaults: { lookback: 100 } },
  VAL:           { params: ['lookback'], defaults: { lookback: 100 } },
  HVN_Distance:  { params: ['lookback'], defaults: { lookback: 100 } },
  LVN_Distance:  { params: ['lookback'], defaults: { lookback: 100 } },

  // Whales
  LargeTradeBuy:  { params: ['minSize', 'period'], defaults: { minSize: 100000, period: 60 } },
  LargeTradeSell: { params: ['minSize', 'period'], defaults: { minSize: 100000, period: 60 } },
  WhaleNetFlow:   { params: ['period', 'minSize'], defaults: { period: 50, minSize: 100000 } },

  // Microstructure
  Absorption:       { params: ['lookback', 'minDelta'], defaults: { lookback: 5,  minDelta: 1000 } },
  StackedImbalance: { params: ['levels', 'ratio'],      defaults: { levels: 3,    ratio: 3 } },
  LiquiditySweep:   { params: ['lookback'],             defaults: { lookback: 20 } },
  VPIN:             { params: ['buckets'],              defaults: { buckets: 50 } },
  KyleLambda:       { params: ['period'],               defaults: { period: 100 } },

  // Range / Consolidation
  ConsolidationRange: { params: ['period'],   defaults: { period: 10 } },
  ConsolidationLow:   { params: ['period'],   defaults: { period: 10 } },
  ConsolidationHigh:  { params: ['period'],   defaults: { period: 10 } },
  PriorBarMove:       { params: ['lookback'], defaults: { lookback: 5 } },

  // Price Change
  ROC:             { params: ['period'],   defaults: { period: 14 } },
  PriceChangePct:  { params: ['period'],   defaults: { period: 1 } },
  PctFromHigh:     { params: ['lookback'], defaults: { lookback: 20 } },
  PctFromLow:      { params: ['lookback'], defaults: { lookback: 20 } },
  PctFromOpen:     { params: [] },
  PctFromVWAP:     { params: [] },
  CumulativeMove:  { params: ['period'],   defaults: { period: 10 } },
  Momentum:        { params: ['period'],   defaults: { period: 10 } },
  PPO:             { params: ['fast', 'slow'], defaults: { fast: 12, slow: 26 } },

};

/**
 * Case-insensitive lookup of an indicator name in INDICATOR_META.
 * Returns the canonical (case-correct) name or null if not found.
 */
function findIndicatorName(name) {
  if (!name) return null;
  if (INDICATOR_META[name]) return name;  // exact match fast path
  const lower = name.toLowerCase();
  const match = Object.keys(INDICATOR_META).find(k => k.toLowerCase() === lower);
  return match || null;
}

// An operand must be a bare NAME or NAME(args) and nothing else. Shared by the
// parser and canRepresentInBuilder so the two always agree on what the builder
// can hold.
const IND_PATTERN = /^([A-Za-z_][A-Za-z0-9_]*)(?:\(([^)]*)\))?$/;

/**
 * Parse one `NAME` / `NAME(args)` token into { name, params, interval }.
 *
 * Module scope rather than nested in parseLogicToConditions because
 * extractIndicatorsFromLogic needs the identical argument reading — especially
 * the quoted-interval rule, which has to match the backend's
 * normalizeLogicExpression or the multi-timeframe round trip loses the tag.
 */
function parseIndicatorToken(token) {
  const m = String(token || '').trim().match(IND_PATTERN);
  if (!m) return { name: '', params: {} };

  const rawName = m[1];
  const canonical = findIndicatorName(rawName);          // case-insensitive lookup
  const name = canonical || rawName;
  const meta = canonical ? INDICATOR_META[canonical] : null;

  const paramStr = m[2] || '';
  const rawVals = paramStr ? paramStr.split(',').map(v => v.trim()) : [];

  // A trailing QUOTED argument shaped like an interval is this indicator's
  // timeframe, not a parameter. Requiring both the quotes and the shape is
  // what leaves RSI(14, "close") alone.
  let interval = null;
  if (rawVals.length) {
    const last = rawVals[rawVals.length - 1];
    const unq = last.replace(/^['"]|['"]$/g, '');
    if (unq !== last && /^\d+[mhdw]$/i.test(unq)) {
      interval = unq.toLowerCase();
      rawVals.pop();
    }
  }

  // Strip quotes and trim — handles RSI(14, 'close') or RSI(14,"close")
  const paramVals = rawVals.map(v => v.replace(/^['"]|['"]$/g, ''));

  const params = {};
  if (meta?.params) {
    meta.params.forEach((pName, idx) => {
      const val = paramVals[idx];
      if (val !== undefined && val !== '') {
        const num = Number(val);
        params[pName] = isNaN(num) ? val : num;
      }
    });

    // Fill in defaults for missing params (AI sometimes omits them)
    if (meta.defaults) {
      for (const [pName, defVal] of Object.entries(meta.defaults)) {
        if (params[pName] === undefined) params[pName] = defVal;
      }
    }
  }

  return { name, params, interval };
}

/**
 * Format an indicator + params into a logic-string token.
 *   RSI  + {period:14}         → "RSI(14)"
 *   VWAP + {}                  → "VWAP"
 *   EMA  + {period:50}         → "EMA(50)"
 *   BB_Upper + {period:20, stdDev:2} → "BB_Upper(20, 2)"
 */
function formatIndicatorToken(name, params = {}, interval = null) {
  const canonical = findIndicatorName(name) || name;
  const meta = INDICATOR_META[canonical];
  // A trailing quoted interval names this indicator's timeframe. The backend
  // recognises it by the quotes AND by the value's shape, which is what keeps
  // it unambiguous against a numeric parameter.
  const iv = interval ? String(interval).trim().toLowerCase() : '';
  const ivArg = /^\d+[mhdw]$/.test(iv) ? `"${iv}"` : '';
  if (!meta || !meta.params?.length) {
    if (!canonical) return '';
    // Parameterless indicators still take an interval: VWAP("1h").
    return ivArg ? `${canonical}(${ivArg})` : canonical;
  }
  const vals = meta.params.map(p => params[p] ?? meta.defaults?.[p] ?? '');
  if (ivArg) vals.push(ivArg);
  return `${canonical}(${vals.join(', ')})`;
}

/**
 * Build a logic string from an array of condition objects.
 *
 * Input shape (per condition):
 *   { indicator, params, operator, value, compareIndicator?, compareParams?, combinator? }
 *
 * Output example:
 *   "RSI(14) < 30 AND EMA(9) > EMA(21)"
 */
export function buildLogicFromConditions(conditions = []) {
  if (!conditions?.length) return '';

  return conditions.map((c, i) => {
    const left = formatIndicatorToken(c.indicator, c.params, c.interval);
    const right = c.compareIndicator
      ? formatIndicatorToken(c.compareIndicator, c.compareParams, c.compareInterval)
      : (c.value ?? '');
    const prefix = i > 0 && c.combinator ? `${c.combinator} ` : '';
    return `${prefix}${left} ${c.operator || '=='} ${right}`;
  }).join(' ');
}

/**
 * Extract a flat list of indicator configs from conditions.
 * Used to tell the backend which indicators to compute.
 *
 * Returns: [{ name, period, source, ...allOtherParams }, ...]
 */
/**
 * Extract a flat, deduplicated list of indicator configs from one or more
 * condition arrays. Used to tell the backend which indicators to compute.
 *
 * Accepts variadic arguments so dedup works across buy/sell/short-open/short-close
 * panels in a single pass:
 *
 *   extractIndicatorsFromConditions(buyConditions)
 *   extractIndicatorsFromConditions(buyConditions, sellConditions)
 *   extractIndicatorsFromConditions(buy, sell, shortOpen, shortClose)
 *
 * Returns: [{ name, period, source, ...allOtherParams }, ...]
 */
/**
 * Shared spec-shaping collector.
 *
 * Both extractors below push through this. They must produce byte-identical
 * specs: the backend keys computed indicators with buildIndicatorKey, and the
 * logic string is normalized to variable names built from the same parts. Two
 * near-copies of this shaping code would drift, and the failure mode is silent
 * — the bot references a variable nothing computed and simply never fires.
 */
function makeIndicatorCollector() {
  const out = [];
  const seen = new Set();

  const push = (name, params = {}, interval = null) => {
    if (!name) return;
    const canonical = findIndicatorName(name) || name;
    const meta = INDICATOR_META[canonical];

    // Session variables are scope inputs, not indicators — the backend fills
    // them in from the bar timestamp. Emitting a spec would ask the engine to
    // compute something it has no implementation for.
    if (meta?.session) return;

    // Build dedup key from name + all param values + source
    const paramStr = Object.entries(params)
      .filter(([k]) => k !== 'source')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('|');
    const key = `${canonical}|${paramStr}|${params.source || 'close'}|${interval || ''}`;
    if (seen.has(key)) return;
    seen.add(key);

    // First param of the indicator's meta becomes the canonical "period"
    // the backend's buildIndicatorKey uses. Parameterless indicators get 0.
    const firstParamName = meta?.params?.[0];
    const periodValue = firstParamName
      ? Number(params[firstParamName] ?? meta?.defaults?.[firstParamName] ?? 0)
      : 0;

    const config = {
      name: canonical,
      period: Number.isFinite(periodValue) ? periodValue : 0,
      source: params.source || 'close',
    };
    // Tells the backend which extra series to roll up. Without it the logic
    // string references EMA_50_1h with nothing computing it.
    if (interval) config.interval = interval;

    // Carry through any additional named params (stdDev, multiplier, minSize, etc.)
    if (meta?.params) {
      for (const pName of meta.params) {
        if (pName === firstParamName) continue;
        const v = params[pName] ?? meta.defaults?.[pName];
        if (v !== undefined) config[pName] = v;
      }
    }

    out.push(config);
  };

  return { out, push };
}

export function extractIndicatorsFromConditions(...conditionArrays) {
  const { out, push } = makeIndicatorCollector();

  // Walk every condition array passed in. One shared `seen` set across all
  // of them means duplicates between buy and sell get deduped correctly.
  for (const arr of conditionArrays) {
    if (!Array.isArray(arr)) continue;
    for (const c of arr) {
      push(c.indicator, c.params, c.interval);
      if (c.compareIndicator) push(c.compareIndicator, c.compareParams, c.compareInterval);
    }
  }

  return out;
}

/**
 * Extract indicator specs directly from raw logic strings.
 *
 * The condition-object extractor cannot serve expression mode: there are no
 * condition objects there, only the string the user typed. This matters more
 * than it looks. A backtest re-derives its indicator list from the logic string
 * (backtest/simulateOHLCV.js extractIndicatorsFromLogic), but a LIVE bot
 * computes only what trade_info.indicators names (file/runBotLogic.js
 * getBotIndicators → deduplicateIndicators). Ship an expression-mode bot with
 * an empty list and it backtests fine, then sits dead in production — every
 * indicator variable in its logic resolves to nothing.
 *
 * This is a token scan, not a structural parse, so arithmetic and conditionals
 * around the indicator calls are irrelevant — which is exactly what expression
 * mode needs. Identifiers that are not known indicators (close, min, abs, AND,
 * numbers) fall out because findIndicatorName returns null for them.
 */
export function extractIndicatorsFromLogic(...logicStrings) {
  const { out, push } = makeIndicatorCollector();
  const TOKEN = /([A-Za-z_][A-Za-z0-9_]*)\s*(\(([^)]*)\))?/g;

  for (const raw of logicStrings) {
    const str = String(raw || '');
    if (!str.trim()) continue;

    TOKEN.lastIndex = 0;
    let m;
    while ((m = TOKEN.exec(str)) !== null) {
      const canonical = findIndicatorName(m[1]);
      if (!canonical) {
        // Not an indicator — min, max, abs, close, AND. Rewind to just past the
        // name instead of skipping the whole match, or a nested indicator call
        // gets swallowed with it: min(ATR(14), 5) would lose ATR entirely, and
        // the resulting bot would reference a series nothing computed.
        // m[1].length > 0 always, so lastIndex still advances.
        TOKEN.lastIndex = m.index + m[1].length;
        continue;
      }
      // Rebuild without the internal whitespace so IND_PATTERN anchors cleanly
      // on "RSI (14)" as well as "RSI(14)".
      const { params, interval } = parseIndicatorToken(`${canonical}${m[2] || ''}`);
      push(canonical, params, interval);
    }
  }

  return out;
}

/**
 * Parse a logic string back into an array of condition objects.
 *
 * Handles formats like:
 *   "RSI(14) < 30 AND EMA(9) > EMA(21)"
 *   "tickPrice > 42000 OR tickRSI(14) < 30"
 *   "Price <= BB_Lower(20, 2)"
 *   "FundingRate < -0.01 AND OI_RSI(14) < 25"
 *
 * Tolerates AI variations:
 *   - Case-insensitive names (rsi, RSI, Rsi)
 *   - Quoted params (RSI(14, 'close') → RSI(14))
 *   - Missing params (BB_Upper(20) → fills stdDev=2 from defaults)
 *   - Names with underscores (OI_RSI, FundingRate)
 */
export function parseLogicToConditions(logicString = '') {
  if (!logicString || typeof logicString !== 'string') return [];

  const str = logicString.trim();
  if (!str) return [];

  // Split on AND / OR while preserving the combinator
  const parts = str.split(/\b(AND|OR)\b/i);

  const segments = [];
  let currentCombinator = null;
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    if (/^(AND|OR)$/i.test(trimmed)) {
      currentCombinator = trimmed.toUpperCase();
      continue;
    }
    segments.push({ text: trimmed, combinator: currentCombinator });
    currentCombinator = null;
  }

  const opPattern = /\s*(<=|>=|==|!=|<|>)\s*/;
  const parseIndicator = parseIndicatorToken;

  return segments.map((seg, i) => {
    const opMatch = seg.text.match(opPattern);
    if (!opMatch) {
      return {
        id: Date.now() + i,
        indicator: seg.text.trim(),
        params: {},
        interval: null,
        operator: '',
        value: '',
        compareIndicator: '',
        compareParams: {},
        combinator: i > 0 ? (seg.combinator || 'AND') : null,
      };
    }

    const operator = opMatch[0].trim();
    const leftStr = seg.text.slice(0, opMatch.index).trim();
    const rightStr = seg.text.slice(opMatch.index + opMatch[0].length).trim();

    const left = parseIndicator(leftStr);
    const right = parseIndicator(rightStr);

    // Right is a number only if parseable AND not a recognized indicator
    const rightAsNumber = Number(rightStr);
    const rightIsKnownIndicator = !!findIndicatorName(right.name);
    const rightIsNumber = Number.isFinite(rightAsNumber) && !rightIsKnownIndicator;

    return {
      id: Date.now() + i,
      indicator: left.name,
      params: left.params,
      // Each side keeps its OWN timeframe: a condition may compare a 1m value
      // against a 1h one, which is the whole point of multi-timeframe.
      interval: left.interval || null,
      operator,
      value: rightIsNumber ? rightStr : '',
      compareIndicator: rightIsNumber ? '' : right.name,
      compareParams: rightIsNumber ? {} : right.params,
      compareInterval: rightIsNumber ? null : (right.interval || null),
      combinator: i > 0 ? (seg.combinator || 'AND') : null,
    };
  });
}
/**
 * Can the flat builder model represent this expression without losing anything?
 *
 * The builder holds a list of `left <op> right` clauses joined by AND/OR, where
 * each operand is a bare indicator token. That cannot express arithmetic on an
 * operand (`SMA(20) * 0.98`) or a conditional (`a ? b : c`), and
 * parseLogicToConditions does not fail on those — it silently returns
 * `{ name: '', params: {} }` for the operand it could not match, so the
 * expression is quietly mangled on the round-trip through the UI.
 *
 * The engine itself has no such limit: normalizeLogicExpression rewrites
 * indicator calls to plain variables and mathjs evaluates the rest, so
 * arithmetic and ternaries already work end-to-end. This function exists purely
 * so the UI can tell when to get out of the way and hand the user the raw
 * string instead of destroying it.
 *
 * Returns true when the builder is safe to use.
 */
export function canRepresentInBuilder(logicString = '') {
  const str = String(logicString || '').trim();
  if (!str) return true;                    // empty is trivially representable

  // A conditional cannot be a flat clause list under any reading.
  if (str.includes('?') || str.includes(':')) return false;

  // Same split the parser uses, so the two agree on what a segment is.
  const segments = str
    .split(/\b(AND|OR)\b/i)
    .map(s => s.trim())
    .filter(s => s && !/^(AND|OR)$/i.test(s));

  if (!segments.length) return false;

  const opPattern = /\s*(<=|>=|==|!=|<|>)\s*/;
  // Deliberately the same anchored pattern parseLogicToConditions uses (via
  // parseIndicatorToken): an operand must be a bare NAME or NAME(args) and
  // nothing else. Anything with arithmetic around it fails here exactly as it
  // fails there.
  const indPattern = IND_PATTERN;

  return segments.every((seg) => {
    const parts = seg.split(opPattern).filter(p => p != null && p.trim() !== '');
    // left, operator, right
    if (parts.length !== 3) return false;
    const [left, , right] = parts.map(p => p.trim());
    if (!indPattern.test(left)) return false;
    // The right side may legitimately be a plain number as well as an indicator.
    return indPattern.test(right) || /^-?\d+(\.\d+)?$/.test(right);
  });
}

/**
 * Syntax-check a raw logic expression, returning an error message or null.
 *
 * Validates the NORMALIZED form. AND/OR/NOT are valid input here but are not
 * mathjs operators — the backend's normalizeLogicExpression rewrites them, so
 * this mirrors that rewrite before parsing. Skipping it would reject every
 * expression the builder itself produces.
 *
 * Only syntax is checked. Indicator names are validated server-side, where the
 * catalog lives.
 */
export function validateLogicExpression(expr) {
  const str = String(expr || '').trim();
  if (!str) return null;                    // empty is allowed (one-sided strategies)
  const normalized = str
    .replace(/\bAND\b/gi, ' and ')
    .replace(/\bOR\b/gi, ' or ')
    .replace(/\bNOT\b/gi, ' not ');
  try { mathParse(normalized); return null; }
  catch (e) { return (e && e.message) || 'Invalid expression'; }
}
